import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PendingReviews.css';
import { api } from '../../utils/authApi';

function PendingReviews() {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [engineerNotes, setEngineerNotes] = useState('');
  const [resolution, setResolution] = useState('');
  const [newStatus, setNewStatus] = useState('In Progress');
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Fetch concerns ONCE on mount
    fetchPendingConcerns();
  }, []); // Empty dependency array = runs only once on mount

  const fetchPendingConcerns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/concerns/all');
            const data = await response.json()
                // Transform the array data to objects and fetch client names
                const transformedConcerns = await Promise.all(data.Message.map(async (concern) => {
                  const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/getClientName/${concern[1]}`, {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  const clientData = await clientResponse.json();
                  
                  return {
                    id: concern[0],
                    clientName: clientData.Message || 'Unknown Client',
                    projectId: concern[2],
                    title: concern[3],
                    category: concern[4],
                    priority: concern[5],
                    description: concern[6],
                    location: concern[7],
                    status: concern[8],
                    engineer_note: concern[9],  
                    engineer_email: concern[10],
                    resolution: concern[11],
                    submittedDate: concern[12],
                    resolvedDate: concern[13]
                  };
                }));
            
           
      
      console.log("Fetched concerns:", transformedConcerns);
      setConcerns(transformedConcerns);
      setError(null);
    } catch (err) {
      setError('Failed to load pending concerns. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.get('/concerns/all');
            const data = await response.json()
                // Transform the array data to objects and fetch client names
                const transformedConcerns = await Promise.all(data.Message.map(async (concern) => {
                  const clientResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/getClientName/${concern[1]}`, {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  const clientData = await clientResponse.json();
                  
                  return {
                    id: concern[0],
                    clientName: clientData.Message || 'Unknown Client',
                    projectId: concern[2],
                    title: concern[3],
                    category: concern[4],
                    priority: concern[5],
                    description: concern[6],
                    location: concern[7],
                    status: concern[8],
                    engineer_note: concern[9],  
                    engineer_email: concern[10],
                    resolution: concern[11],
                    submittedDate: concern[12],
                    resolvedDate: concern[13]
                  };
                }));
            
            setConcerns(transformedConcerns);
            setError(null);
      
      // Clear selection if the selected concern was updated by someone else
      if (selectedConcern) {
        const updatedConcern = transformedConcerns.find(c => c.id === selectedConcern.id);
        if (updatedConcern) {
          setSelectedConcern(updatedConcern);
        } else {
          setSelectedConcern(null);
        }
      }
    } catch (err) {
      setError('Failed to refresh concerns.');
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const pendingConcerns = concerns.filter(c => c.status === 'Pending');

  const handleSelectConcern = (concern) => {
    setSelectedConcern(concern);
    setEngineerNotes('');
    setResolution('');
    setNewStatus(concern.status === 'Pending' ? 'In Progress' : concern.status);
  };

  const handleUpdateStatus = async () => {
    if (!selectedConcern) return;

    setUpdating(true);

    try {
      const userInfoString = localStorage.getItem('user_info');
      const userInfo = JSON.parse(userInfoString);
      const engineerEmail = userInfo.email;
      // Prepare update payload
      const updatePayload = {
        status: newStatus,
        engineer_note: engineerNotes || null,
        engineer_email:engineerEmail
      };

      // Add resolution and resolved_date only if status is Resolved
      if (newStatus === 'Resolved') {
        updatePayload.resolution = resolution || null;
        updatePayload.resolved_date = new Date().toISOString();
      }

      console.log('Updating concern with payload:', updatePayload);

      // Call API to update concern (JWT token automatically included)
      // Engineer email is automatically extracted from token on backend
      const response = await api.put(`/concerns/updateConcern/${selectedConcern.id}`, updatePayload);
      
      if (!response.ok) {
        throw new Error('Failed to update concern');
      }

      // Get engineer info from token for optimistic update
      

      // OPTIMISTIC UPDATE - Update local state immediately
      const updatedConcerns = concerns.map(c => {
        if (c.id === selectedConcern.id) {
          return {
            ...c,
            status: newStatus,
            engineer_note: engineerNotes,
            engineer_email: engineerEmail,
            resolution: newStatus === 'Resolved' ? resolution : c.resolution,
            resolved_date: newStatus === 'Resolved' ? new Date().toISOString() : c.resolved_date
          };
        }
        return c;
      });

      setConcerns(updatedConcerns);
      alert('Concern updated successfully!');
      
      // Clear selection and form
      setSelectedConcern(null);
      setEngineerNotes('');
      setResolution('');
      setNewStatus('In Progress');

    } catch (error) {
      console.error('Error updating concern:', error);
      alert('Failed to update concern. Please try again.');
      
      // ONLY refetch on error to restore correct state
      await fetchPendingConcerns();
      
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityClass = (priority) => {
    return `priority-badge priority-${priority}`;
  };

  if (loading) {
    return (
      <div className="pending-reviews-page">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </nav>
        <div className="page-content">
          <div className="container">
            <div className="loading-message">Loading pending concerns...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pending-reviews-page">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </nav>
        <div className="page-content">
          <div className="container">
            <div className="error-message">
              {error}
              <button onClick={fetchPendingConcerns} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-reviews-page">
      <nav className="dashboard-nav">
        <div className="container nav-content">
          <h2 className="logo">EngineeringCubes</h2>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleManualRefresh} 
              className="btn btn-secondary"
              disabled={refreshing}
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button onClick={() => navigate('/engineer-dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="page-content">
        <div className="container">
          <h1 className="page-title">Pending Reviews</h1>
          <p className="page-subtitle">
            {pendingConcerns.length} concern{pendingConcerns.length !== 1 ? 's' : ''} awaiting review
          </p>

          <div className="reviews-layout">
            {/* Left Side - List of Concerns */}
            <div className="concerns-list-panel">
              {pendingConcerns.length === 0 ? (
                <div className="empty-state">
                  <h3>No pending concerns</h3>
                  <p>All concerns have been reviewed!</p>
                </div>
              ) : (
                pendingConcerns.map((concern) => (
                  <div
                    key={concern.id}
                    className={`concern-item ${selectedConcern?.id === concern.id ? 'selected' : ''}`}
                    onClick={() => handleSelectConcern(concern)}
                  >
                    <div className="concern-item-header">
                      <h3>{concern.title}</h3>
                      <span className={getPriorityClass(concern.priority)}>
                        {concern.priority}
                      </span>
                    </div>
                    <div className="concern-item-meta">
                      <span className="client-name">Client Name: {concern.clientName}</span>
                      <span className="separator">•</span>
                      <span className="category">{concern.category}</span>
                    </div>
                    <div className="concern-item-date">
                      {formatDate(concern.submittedDate)}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Right Side - Concern Details */}
            <div className="concern-details-panel">
              {selectedConcern ? (
                <>
                  <div className="detail-header">
                    <div>
                      <h2>{selectedConcern.title}</h2>
                      <div className="detail-badges">
                        <span className={getPriorityClass(selectedConcern.priority)}>
                          {selectedConcern.priority}
                        </span>
                        <span className="category-badge">{selectedConcern.category}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Concern Information</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Client Name:</span>
                        <span className="value">{selectedConcern.clientName}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Project ID:</span>
                        <span className="value">{selectedConcern.projectId}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Location:</span>
                        <span className="value">{selectedConcern.location}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Submitted:</span>
                        <span className="value">{formatDate(selectedConcern.submittedDate)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h3>Description</h3>
                    <p className="description-text">{selectedConcern.description}</p>
                  </div>

                  <div className="detail-section">
                    <h3>Engineer Notes</h3>
                    <textarea
                      value={engineerNotes}
                      onChange={(e) => setEngineerNotes(e.target.value)}
                      placeholder="Add your notes and observations here..."
                      rows="4"
                      className="notes-textarea"
                    />
                  </div>

                  <div className="detail-section">
                    <h3>Update Status</h3>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                    </select>
                  </div>

                  {/* Show Resolution field only when status is Resolved */}
                  {newStatus === 'Resolved' && (
                    <div className="detail-section">
                      <h3>Resolution</h3>
                      <textarea
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Describe how the concern was resolved..."
                        rows="4"
                        className="notes-textarea"
                      />
                    </div>
                  )}

                  <div className="detail-actions">
                    <button 
                      onClick={handleUpdateStatus} 
                      className="btn btn-primary"
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : 'Update Concern'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <h3>Select a concern to review</h3>
                  <p>Click on a concern from the list to view details and update status</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PendingReviews;